import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { widget as api } from '@/api/endpoints'
import { Alert, Checkbox, Field, Input, Select, Spinner, Textarea } from '@/components/ui'
import { PlanLock, usePlanFeature } from '@/features/billing/PlanFeature'
import type { WidgetConfig } from '@/types/api'
import { useCenters } from '@/features/centers/useCenters'

/**
 * Widget na online rezerváciu — nastavenie pre správcu.
 *
 * Obrazovka je postavená okolo jednej otázky, ktorú si škola pri zapínaní
 * kladie: *koľko toho na seba pustím?* Preto sú domény a stropy vyššie než
 * kozmetika; farba a uvítanie sú až na konci.
 */
export default function WidgetTab() {
  const { t } = useTranslation()
  const { multi: multiCenter, active: activeCenters } = useCenters()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['widget-config'], queryFn: api.config })

  const [form, setForm] = useState<Partial<WidgetConfig>>({})
  const [origins, setOrigins] = useState('')
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const widget = usePlanFeature('widget').allowed

  useEffect(() => {
    if (!data) return
    setForm(data)
    // Domény sa editujú ako text, jedna na riadok. Zoznam s tlačidlami by bol
    // pri troch položkách zbytočná mašinéria a horšie sa doň vkladá zo schránky.
    setOrigins(data.origins.join('\n'))
  }, [data])

  const save = useMutation({
    mutationFn: (payload: Partial<WidgetConfig>) => api.save(payload),
    onSuccess: (saved) => {
      setMessage({ kind: 'success', text: t('global.saved') })
      setOrigins(saved.origins.join('\n'))
      void qc.invalidateQueries({ queryKey: ['widget-config'] })
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  if (isLoading || !data) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  function set<K extends keyof WidgetConfig>(key: K, value: WidgetConfig[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const originList = origins
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  const live = Boolean(form.enabled) && originList.length > 0

  async function copySnippet() {
    await navigator.clipboard.writeText(data!.snippet)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }

  return (
    <form
      className="max-w-3xl space-y-5"
      onSubmit={(e) => {
        e.preventDefault()
        setMessage(null)
        save.mutate({ ...form, origins: originList })
      }}
    >
      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      {/* Stav hore: zapnutý widget bez domén nefunguje a nie je to vidieť
          na ničom inom — rám sa načíta, ale prehliadač ho na stránke školy
          odmietne zobraziť. */}
      {form.enabled && originList.length === 0 ? (
        <Alert variant="error">{t('widget.noOriginsWarning')}</Alert>
      ) : null}

      <section className="card space-y-3 p-4">
        <Checkbox
          checked={Boolean(form.enabled)}
          disabled={!widget && !form.enabled}
          onChange={(e) => set('enabled', e.target.checked)}
          label={t('widget.enabled')}
        />
        <PlanLock feature="widget" />
        <p className="text-xs text-slate-500">{t('widget.enabledHint')}</p>
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('widget.modeTitle')}</h2>

        <Field label={t('widget.mode')} hint={t('widget.modeHint')}>
          <Select
            value={form.mode ?? 'form'}
            onChange={(e) => set('mode', e.target.value as WidgetConfig['mode'])}
          >
            {data.modes.map((mode) => (
              <option
                key={mode.value}
                value={mode.value}
                // Rozhovor bez nastaveného modelu by bol okno, do ktorého sa
                // dá písať, ale nikto neodpovie.
                disabled={mode.value === 'chat' && !data.chat_available}
              >
                {mode.label}
              </option>
            ))}
          </Select>
        </Field>
        {/* Widget s formulárom je od Štandardu, AI chat v ňom od Profi. */}
        <PlanLock feature="ai_chat" />

        {/* Nezáleží na tom, aký režim je práve zvolený. Keď model nie je
            k dispozícii, možnosť „Rozhovor“ je neaktívna — a bez tejto vety
            by sa škola nedozvedela prečo. Podmienka na `form.mode === 'chat'`
            by vetu ukázala len tomu, kto ten režim už má zapnutý, čiže presne
            tomu, kto vysvetlenie nepotrebuje. */}
        {!data.chat_available ? (
          <Alert variant="warning">{t('widget.chatUnavailable')}</Alert>
        ) : null}

        {form.mode === 'chat' ? (
          <>
            {/* Suma je v mene školy — cenník modelu je síce v dolároch, ale
                prepočet aj marža sú už v uloženej sume. Bez uvedenej meny je
                z čísla hádanka: `12` môže byť dvanásť eur aj dvanásť dolárov. */}
            <div className="rounded bg-slate-50 p-3 text-xs text-slate-600">
              <div className="font-medium text-slate-700">{t('widget.usageTitle')}</div>
              <p className="mt-1">
                {t('widget.usageLine', {
                  conversations: data.usage.conversations,
                  bookings: data.usage.bookings,
                  charge: `${data.usage.charge} ${data.usage.currency}`,
                })}
              </p>
              {data.usage.bookings > 0 ? (
                <p>
                  {t('widget.usagePerBooking', {
                    value: `${data.usage.per_booking} ${data.usage.currency}`,
                  })}
                </p>
              ) : null}
              <p className="mt-1 text-slate-500">
                {t('widget.usageCurrencyHint', { currency: data.usage.currency })}
              </p>
            </div>

            {/* Rozhovor je jediná časť systému, ktorá stojí peniaze za každé
                použitie a je otvorená komukoľvek bez prihlásenia. Tieto dve
                čísla sú to, čo stojí medzi školou a faktúrou za cudzí
                experiment. */}
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label={t('widget.chatIpLimit')} hint={t('widget.chatIpLimitHint')}>
                <Input
                  type="number"
                  min={0}
                  value={form.chat_ip_day_limit ?? 5}
                  onChange={(e) => set('chat_ip_day_limit', Number(e.target.value))}
                />
              </Field>
              <Field
                label={`${t('widget.chatBudget')} (${data.usage.currency})`}
                hint={t('widget.chatBudgetHint')}
              >
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.chat_month_budget ?? 0}
                    onChange={(e) => set('chat_month_budget', e.target.value)}
                  />
                  <span className="text-sm text-slate-600">{data.usage.currency}</span>
                </div>
              </Field>
            </div>
          </>
        ) : null}
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('widget.embedTitle')}</h2>
        <p className="text-xs text-slate-500">{t('widget.embedHint')}</p>

        <div className="flex items-start gap-2">
          <code className="flex-1 overflow-x-auto rounded bg-slate-100 px-3 py-2 text-xs">
            {data.snippet}
          </code>
          <button type="button" className="btn btn-ghost text-xs" onClick={copySnippet}>
            {copied ? t('widget.copied') : t('widget.copy')}
          </button>
        </div>
        {/* Web jedného strediska (`sql/040`) - `data-center` v kóde. */}
        {multiCenter ? (
          <div className="space-y-1 text-xs text-slate-600">
            <p>{t('centers.widgetHint')}</p>
            <ul className="space-y-0.5">
              {activeCenters.map((c) => (
                <li key={c.id}>
                  <strong className="font-medium">{c.name}</strong>:{' '}
                  <code className="rounded bg-slate-100 px-1">data-center="{c.id}"</code>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <p className="text-xs text-slate-500">
          {t('widget.previewHint')}{' '}
          <a
            href={data.widget_url}
            target="_blank"
            rel="noreferrer"
            className="text-action-700 underline"
          >
            {data.widget_url}
          </a>
        </p>
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('widget.originsTitle')}</h2>
        <p className="text-xs text-slate-500">{t('widget.originsHint')}</p>

        <Field label={t('widget.origins')}>
          <Textarea
            rows={4}
            value={origins}
            spellCheck={false}
            placeholder={'https://skola.sk\nhttps://www.skola.sk'}
            onChange={(e) => setOrigins(e.target.value)}
          />
        </Field>
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('widget.limitsTitle')}</h2>
        <p className="text-xs text-slate-500">{t('widget.limitsHint')}</p>

        <Field label={t('widget.capacityMode')}>
          <Select
            value={form.capacity_mode ?? 'limit'}
            onChange={(e) => set('capacity_mode', e.target.value as WidgetConfig['capacity_mode'])}
          >
            {data.capacity_modes.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-2 sm:grid-cols-2">
          <Field label={t('widget.dailyLimit')}>
            <Input
              type="number"
              min={1}
              value={form.daily_limit ?? 5}
              onChange={(e) => set('daily_limit', Number(e.target.value))}
            />
          </Field>
          <Field label={t('widget.slotLimit')}>
            <Input
              type="number"
              min={1}
              value={form.slot_limit ?? 2}
              onChange={(e) => set('slot_limit', Number(e.target.value))}
            />
          </Field>
          <Field label={t('widget.leadHours')}>
            <Input
              type="number"
              min={0}
              value={form.lead_hours ?? 12}
              onChange={(e) => set('lead_hours', Number(e.target.value))}
            />
          </Field>
          <Field label={t('widget.horizonDays')}>
            <Input
              type="number"
              min={1}
              value={form.horizon_days ?? 60}
              onChange={(e) => set('horizon_days', Number(e.target.value))}
            />
          </Field>
          <Field label={t('widget.maxPersons')} hint={t('widget.maxPersonsHint')}>
            <Input
              type="number"
              min={0}
              value={form.max_persons ?? 0}
              onChange={(e) => set('max_persons', Number(e.target.value))}
            />
          </Field>
        </div>
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('widget.noticeTitle')}</h2>

        <Field label={t('widget.adminNotice')}>
          <Select
            value={form.admin_notice ?? 'each'}
            onChange={(e) => set('admin_notice', e.target.value as WidgetConfig['admin_notice'])}
          >
            {data.admin_notices.map((notice) => (
              <option key={notice.value} value={notice.value}>
                {notice.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('widget.replyTo')} hint={t('widget.replyToHint')}>
          <Input
            type="email"
            value={form.reply_to ?? ''}
            onChange={(e) => set('reply_to', e.target.value)}
          />
        </Field>
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('widget.lookTitle')}</h2>

        <Field label={t('widget.greeting')} hint={t('widget.greetingHint')}>
          <Textarea
            rows={3}
            value={form.greeting ?? ''}
            onChange={(e) => set('greeting', e.target.value)}
          />
        </Field>

        <Field label={t('widget.accent')} hint={t('widget.accentHint')}>
          <div className="flex items-center gap-2">
            <input
              type="color"
              className="h-8 w-12 rounded border border-slate-300"
              value={form.accent || '#a4091e'}
              onChange={(e) => set('accent', e.target.value)}
            />
            <Input
              className="w-32"
              value={form.accent ?? ''}
              placeholder="#a4091e"
              onChange={(e) => set('accent', e.target.value)}
            />
          </div>
        </Field>
      </section>

      <div className="flex items-center gap-3">
        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          {save.isPending ? <Spinner className="h-4 w-4" /> : t('global.save')}
        </button>
        <span className="text-xs text-slate-500">
          {live ? t('widget.statusLive') : t('widget.statusOff')}
        </span>
      </div>
    </form>
  )
}
